import { useState, type ReactNode } from "react";
import { cn } from "@/cn";
import type { PromptTemplate } from "@/api/prompts";
import { renderMarkdown } from "@/lib/markdown";
import {
  categoryColor,
  categoryLabel,
  formatPromptDate,
  parsePatternLabel,
  promptContextRequirements,
  promptRequirementSummary,
} from "@/lib/prompt-ui";

type PromptViewMode = "formatted" | "raw";

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
    >
      {copied ? "Copied!" : label}
    </button>
  );
}

function PlaceholderText({ text }: { text: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/g);

  return (
    <>
      {parts.map((part, index) =>
        /^\{\{[^}]+\}\}$/.test(part) ? (
          <span
            key={index}
            className="rounded bg-brand/10 px-1 py-0.5 font-mono text-[11px] text-brand"
          >
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

function PromptTextPanel({ prompt }: { prompt: PromptTemplate }) {
  const [viewMode, setViewMode] = useState<PromptViewMode>("formatted");
  const systemPrompt = prompt.systemPrompt?.trim() || "";

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            System prompt
          </h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Read-only view of the active template stored in DynamoDB.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-border p-1">
            {(["formatted", "raw"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs font-medium transition",
                  viewMode === mode
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {mode === "formatted" ? "Formatted" : "Raw"}
              </button>
            ))}
          </div>
          {systemPrompt && <CopyButton text={systemPrompt} label="Copy prompt" />}
        </div>
      </div>

      <div className="max-h-[min(70vh,640px)] overflow-y-auto px-5 py-4">
        {!systemPrompt ? (
          <p className="text-sm text-muted-foreground">(No system prompt text)</p>
        ) : viewMode === "raw" ? (
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
            <PlaceholderText text={systemPrompt} />
          </pre>
        ) : (
          <div
            className="prose-sm max-w-none text-foreground [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(systemPrompt) }}
          />
        )}
      </div>
    </div>
  );
}

function MetaSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-foreground">{value}</dd>
    </div>
  );
}

export function PromptDetailContent({ prompt }: { prompt: PromptTemplate }) {
  const contextFlags = promptContextRequirements(prompt);
  const createdLabel = formatPromptDate(prompt.createdAt);
  const updatedLabel = formatPromptDate(prompt.updatedAt);

  return (
    <div className="lg:grid lg:grid-cols-3 lg:gap-6">
      <div className="mb-6 space-y-4 lg:col-span-1 lg:mb-0">
        <MetaSection title="Template">
          <dl className="space-y-3">
            <MetaRow label="Kind" value={<code className="text-xs">{prompt.kind}</code>} />
            <MetaRow label="Version" value={`v${prompt.version}`} />
            <MetaRow
              label="Status"
              value={
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px]",
                    prompt.isActive
                      ? "bg-success/10 text-success"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {prompt.isActive ? "Active" : "Inactive"}
                </span>
              }
            />
            {prompt.category && (
              <MetaRow
                label="Category"
                value={
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-medium",
                      categoryColor(prompt.category),
                    )}
                  >
                    {categoryLabel(prompt.category)}
                  </span>
                }
              />
            )}
          </dl>
        </MetaSection>

        <MetaSection title="Context required">
          <p className="mb-2 text-xs text-muted-foreground">{promptRequirementSummary(prompt)}</p>
          {contextFlags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {contextFlags.map((flag) => (
                <span
                  key={flag}
                  className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                >
                  {flag}
                </span>
              ))}
            </div>
          )}
          {prompt.requiresContext && prompt.requiresContext.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Context files
              </p>
              <ul className="space-y-1">
                {prompt.requiresContext.map((ctx) => (
                  <li key={ctx}>
                    <code className="text-[11px] text-muted-foreground">{ctx}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </MetaSection>

        <MetaSection title="Execution">
          <dl className="grid gap-3">
            <MetaRow label="Parse pattern" value={parsePatternLabel(prompt.parsePattern)} />
            <MetaRow label="Timeout" value={`${prompt.maxDuration}s`} />
            {prompt.model && <MetaRow label="Model" value={prompt.model} />}
            {prompt.allowedTools && prompt.allowedTools.length > 0 && (
              <MetaRow
                label="Allowed tools"
                value={prompt.allowedTools.join(", ")}
              />
            )}
            {prompt.disallowedTools && prompt.disallowedTools.length > 0 && (
              <MetaRow
                label="Disallowed tools"
                value={prompt.disallowedTools.join(", ")}
              />
            )}
          </dl>
        </MetaSection>

        {(createdLabel || updatedLabel) && (
          <MetaSection title="Metadata">
            <dl className="grid gap-3">
              {createdLabel && <MetaRow label="Created" value={createdLabel} />}
              {updatedLabel && <MetaRow label="Updated" value={updatedLabel} />}
            </dl>
          </MetaSection>
        )}
      </div>

      <div className="space-y-4 lg:col-span-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-bold text-foreground">{prompt.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {prompt.description || "No description available."}
          </p>
        </div>

        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs text-muted-foreground">
          Placeholders like{" "}
          <code className="rounded bg-muted/40 px-1 py-0.5 font-mono text-[10px] text-brand">
            {"{{PROFILE_NARRATIVE}}"}
          </code>{" "}
          and{" "}
          <code className="rounded bg-muted/40 px-1 py-0.5 font-mono text-[10px] text-brand">
            {"{{input}}"}
          </code>{" "}
          are substituted with your profile, CV, and job data when the prompt runs.
        </div>

        <PromptTextPanel prompt={prompt} />

        {prompt.outputContract && (
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Output contract
              </h3>
              <CopyButton text={prompt.outputContract} label="Copy" />
            </div>
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-bg px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
              {prompt.outputContract}
            </pre>
          </div>
        )}

        {prompt.changelog && (
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Changelog
            </h3>
            <p className="text-sm text-muted-foreground">{prompt.changelog}</p>
          </div>
        )}
      </div>
    </div>
  );
}
